import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import {
  Lock, ArrowsClockwise, PencilSimple, Sparkle, Crown, MagnifyingGlass,
  CaretDown, CaretRight, CaretLeft, DownloadSimple, Trash, FrameCorners, Code,
  FloppyDisk, GitBranch, X as XIcon, Note, TextT, ArrowUUpLeft, ArrowUUpRight,
  Copy, Palette, FilePdf, PaintBucket, Square,
} from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  ReactFlow, ReactFlowProvider, Background, Controls, MiniMap, Panel,
  useNodesState, useEdgesState, addEdge, Handle, Position, MarkerType,
  getNodesBounds, getViewportForBounds, EdgeLabelRenderer,
  BaseEdge, getSmoothStepPath, ConnectionMode, NodeToolbar, NodeResizer,
} from '@xyflow/react';
import { toPng } from 'html-to-image';
import { jsPDF } from 'jspdf';
import '@xyflow/react/dist/style.css';
import { useConfirm } from './ConfirmDialog';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from '../components/ui/dropdown-menu';

// ─── Hover-handle constants ───────────────────────────────────────
// Single handle per side. With ConnectionMode.Loose, react-flow lets the
// connection start AND end on the same handle type, so we don't need
// separate target stubs. Source = drag origin, Target = drop site —
// arrowhead lands on the drop side, which is what users expect.
const HANDLE_STYLE = {
  width: 12, height: 12, background: '#6366f1', border: '2px solid white',
  boxShadow: '0 0 0 2px rgba(99,102,241,.25)',
  transition: 'transform 120ms ease',
};

function FourHandles() {
  return (
    <>
      <Handle type="source" position={Position.Top}    id="t" style={HANDLE_STYLE} className="wb-handle" />
      <Handle type="source" position={Position.Right}  id="r" style={HANDLE_STYLE} className="wb-handle" />
      <Handle type="source" position={Position.Bottom} id="b" style={HANDLE_STYLE} className="wb-handle" />
      <Handle type="source" position={Position.Left}   id="l" style={HANDLE_STYLE} className="wb-handle" />
    </>
  );
}

const COLOR = {
  white:    { bg: '#ffffff', border: '#52525b', text: '#27272a' },
  // Saturated palette (default/strong fills)
  indigo:   { bg: '#eef2ff', border: '#6366f1', text: '#3730a3' },
  emerald:  { bg: '#d1fae5', border: '#10b981', text: '#065f46' },
  amber:    { bg: '#fef3c7', border: '#f59e0b', text: '#92400e' },
  rose:     { bg: '#ffe4e6', border: '#f43f5e', text: '#9f1239' },
  slate:    { bg: '#f1f5f9', border: '#64748b', text: '#1e293b' },
  violet:   { bg: '#ede9fe', border: '#8b5cf6', text: '#5b21b6' },
  sky:      { bg: '#e0f2fe', border: '#0ea5e9', text: '#075985' },
  // Soft pastels (light tones — lighter than the saturated row above)
  pastelPink:   { bg: '#fce7f3', border: '#f472b6', text: '#9d174d' },
  pastelPeach:  { bg: '#ffedd5', border: '#fb923c', text: '#9a3412' },
  pastelMint:   { bg: '#ccfbf1', border: '#2dd4bf', text: '#115e59' },
  pastelLilac:  { bg: '#f3e8ff', border: '#c084fc', text: '#6b21a8' },
  pastelSky:    { bg: '#dbeafe', border: '#60a5fa', text: '#1e40af' },
  pastelLemon:  { bg: '#fef9c3', border: '#facc15', text: '#854d0e' },
};
const colorOf = (k) => COLOR[k] || COLOR.white;

// ─── Generic SVG shape node (covers most flowchart/basic shapes) ───
// Each shape type renders an SVG path/polygon at fixed dimensions, with
// label centered. Handles overlay on top so connection still works.
const SHAPE_GEOMETRY = {
  rectangle:    { w: 140, h: 70,  draw: (c, sw=2, ss='solid') => `<rect x='1' y='1' width='138' height='68' rx='4' fill='${c.bg}' stroke='${c.border}' stroke-width='${sw}' ${dashFor(ss)} />` },
  rounded:      { w: 140, h: 70,  draw: (c, sw=2, ss='solid') => `<rect x='1' y='1' width='138' height='68' rx='14' fill='${c.bg}' stroke='${c.border}' stroke-width='${sw}' ${dashFor(ss)} />` },
  pill:         { w: 140, h: 60,  draw: (c, sw=2, ss='solid') => `<rect x='1' y='1' width='138' height='58' rx='29' fill='${c.bg}' stroke='${c.border}' stroke-width='${sw}' ${dashFor(ss)} />` },
  circle:       { w: 110, h: 110, draw: (c, sw=2, ss='solid') => `<circle cx='55' cy='55' r='52' fill='${c.bg}' stroke='${c.border}' stroke-width='${sw}' ${dashFor(ss)} />` },
  ellipse:      { w: 140, h: 80,  draw: (c, sw=2, ss='solid') => `<ellipse cx='70' cy='40' rx='68' ry='38' fill='${c.bg}' stroke='${c.border}' stroke-width='${sw}' ${dashFor(ss)} />` },
  diamond:      { w: 140, h: 100, draw: (c, sw=2, ss='solid') => `<polygon points='70,4 136,50 70,96 4,50' fill='${c.bg}' stroke='${c.border}' stroke-width='${sw}' ${dashFor(ss)} />` },
  triangle:     { w: 130, h: 110, draw: (c, sw=2, ss='solid') => `<polygon points='65,5 125,105 5,105' fill='${c.bg}' stroke='${c.border}' stroke-width='${sw}' ${dashFor(ss)} />` },
  triangleDown: { w: 130, h: 110, draw: (c, sw=2, ss='solid') => `<polygon points='5,5 125,5 65,105' fill='${c.bg}' stroke='${c.border}' stroke-width='${sw}' ${dashFor(ss)} />` },
  hexagon:      { w: 140, h: 80,  draw: (c, sw=2, ss='solid') => `<polygon points='30,4 110,4 136,40 110,76 30,76 4,40' fill='${c.bg}' stroke='${c.border}' stroke-width='${sw}' ${dashFor(ss)} />` },
  pentagon:     { w: 130, h: 110, draw: (c, sw=2, ss='solid') => `<polygon points='65,4 126,46 100,106 30,106 4,46' fill='${c.bg}' stroke='${c.border}' stroke-width='${sw}' ${dashFor(ss)} />` },
  parallelogram:{ w: 150, h: 80,  draw: (c, sw=2, ss='solid') => `<polygon points='30,4 146,4 120,76 4,76' fill='${c.bg}' stroke='${c.border}' stroke-width='${sw}' ${dashFor(ss)} />` },
  trapezoid:    { w: 150, h: 80,  draw: (c, sw=2, ss='solid') => `<polygon points='30,4 120,4 146,76 4,76' fill='${c.bg}' stroke='${c.border}' stroke-width='${sw}' ${dashFor(ss)} />` },
  arrowRight:   { w: 150, h: 70,  draw: (c, sw=2, ss='solid') => `<polygon points='4,18 110,18 110,4 146,35 110,66 110,52 4,52' fill='${c.bg}' stroke='${c.border}' stroke-width='${sw}' ${dashFor(ss)} />` },
  arrowDouble:  { w: 170, h: 70,  draw: (c, sw=2, ss='solid') => `<polygon points='35,18 35,4 5,35 35,66 35,52 135,52 135,66 165,35 135,4 135,18' fill='${c.bg}' stroke='${c.border}' stroke-width='${sw}' stroke-linejoin='round' ${dashFor(ss)} />` },
  star:         { w: 120, h: 110, draw: (c, sw=2, ss='solid') => `<polygon points='60,5 75,40 113,46 84,72 92,108 60,89 28,108 36,72 7,46 45,40' fill='${c.bg}' stroke='${c.border}' stroke-width='${sw}' ${dashFor(ss)} />` },
  cloud:        { w: 150, h: 90,  draw: (c, sw=2, ss='solid') => `<path d='M30 70 Q5 70 8 50 Q-5 30 20 28 Q22 8 50 14 Q72 -2 92 18 Q120 12 122 38 Q145 42 130 65 Q135 82 110 78 Z' fill='${c.bg}' stroke='${c.border}' stroke-width='${sw}' ${dashFor(ss)} />` },
  cylinder:     { w: 130, h: 90,  draw: (c, sw=2, ss='solid') => `<g><ellipse cx='65' cy='12' rx='60' ry='10' fill='${c.bg}' stroke='${c.border}' stroke-width='${sw}' ${dashFor(ss)} /><path d='M 5 12 L 5 78 Q 5 88 65 88 Q 125 88 125 78 L 125 12' fill='${c.bg}' stroke='${c.border}' stroke-width='${sw}' ${dashFor(ss)} /><ellipse cx='65' cy='12' rx='60' ry='10' fill='none' stroke='${c.border}' stroke-width='${sw}' ${dashFor(ss)} /></g>` },
  document:     { w: 140, h: 90,  draw: (c, sw=2, ss='solid') => `<path d='M4 4 L136 4 L136 75 Q105 90 70 75 Q35 60 4 75 Z' fill='${c.bg}' stroke='${c.border}' stroke-width='${sw}' ${dashFor(ss)} />` },
  callout:      { w: 140, h: 80,  draw: (c, sw=2, ss='solid') => `<path d='M4 4 L136 4 L136 56 L48 56 L36 76 L34 56 L4 56 Z' fill='${c.bg}' stroke='${c.border}' stroke-width='${sw}' ${dashFor(ss)} />` },
  database:     { w: 130, h: 90,  draw: (c, sw=2, ss='solid') => `<g><ellipse cx='65' cy='12' rx='60' ry='10' fill='${c.bg}' stroke='${c.border}' stroke-width='${sw}' ${dashFor(ss)} /><path d='M 5 12 L 5 78 Q 5 88 65 88 Q 125 88 125 78 L 125 12' fill='${c.bg}' stroke='${c.border}' stroke-width='${sw}' ${dashFor(ss)} /><ellipse cx='65' cy='12' rx='60' ry='10' fill='none' stroke='${c.border}' stroke-width='${sw}' ${dashFor(ss)} /></g>` },
};
const dashFor = (s) => s === 'dashed' ? `stroke-dasharray='8,4'` : (s === 'dotted' ? `stroke-dasharray='2,3'` : '');

// Shape node — renders the geometry via dangerouslySetInnerHTML SVG and overlays the label.
// Selection ring is applied to a wrapping div (outline + box-shadow) so it's visible
// even on shapes whose geometry is non-rectangular (diamond, triangle, etc.).
function ShapeNode({ data, selected, id, width, height }) {
  const c = colorOf(data.color);
  const shape = data.shape || 'rectangle';
  const g = SHAPE_GEOMETRY[shape] || SHAPE_GEOMETRY.rectangle;
  const w = width || g.w;
  const h = height || g.h;
  // Render with optional override colors so fill, border, and text are independent.
  const fill = data.fillColor || c.bg;
  const stroke = data.borderColor || c.border;
  const textColor = data.textColor || c.text;
  return (
    <div
      className="relative"
      style={{ width: w, height: h, borderRadius: 6 }}
    >
      <NodeResizer
        minWidth={60} minHeight={40}
        isVisible={selected}
        // Lucid-style: thin border + small white-filled corner squares.
        lineStyle={{ borderColor: '#3b82f6', borderWidth: 1 }}
        handleStyle={{ width: 7, height: 7, background: '#ffffff', border: '1px solid #3b82f6', borderRadius: 1 }}
      />
      <svg
        viewBox={`0 0 ${g.w} ${g.h}`}
        preserveAspectRatio="none"
        className="absolute inset-0 w-full h-full"
        style={{ overflow: 'visible' }}
        dangerouslySetInnerHTML={{ __html: g.draw({ bg: fill, border: stroke, text: textColor }, data.borderWidth || 2, data.borderStyle || 'solid') }}
      />
      <NodeLabel id={id} data={data} color={textColor} fontSize={data.fontSize || 12} weight={data.bold ? '700' : '600'} italic={data.italic} underline={data.underline} />
      <FourHandles />
    </div>
  );
}

// ─── Format bar — Lucid-style top edit toolbar ───────────────
// Appears between the toolbar and the canvas body when a node or edge
// is selected. Replaces the floating per-node toolbar (now removed).
const FONT_SIZES = [10, 12, 14, 16, 18, 22, 28, 36];
const BORDER_WIDTHS = [1, 2, 3, 4];
const BORDER_STYLES = [
  { id: 'solid',  preview: '———' },
  { id: 'dashed', preview: '— —' },
  { id: 'dotted', preview: '· · ·' },
];

// ─── Compact color dropdown (Lucid-style) ────────────────────
// Single button with current color preview; opens a 14-swatch grid
function ColorDropdown({ value, onChange, allowNone = false, icon, title, testid }) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button title={title}
          className="h-7 px-1.5 rounded hover:bg-zinc-100 inline-flex items-center gap-1 text-zinc-600"
          data-testid={testid}>
          {icon}
          <div className="w-3 h-3 rounded-sm border border-zinc-300" style={{ background: value || 'transparent' }} />
          <CaretDown size={9} />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="p-2 w-auto">
        <div className="text-[10px] uppercase tracking-wider font-bold text-zinc-400 mb-1.5 px-1">{title}</div>
        <div className="grid grid-cols-7 gap-1.5 mb-1.5">
          {allowNone && (
            <button onClick={() => onChange(null)} title="No fill"
              className="w-5 h-5 rounded-full bg-white border border-zinc-200 hover:scale-110 transition-transform relative"
              data-testid={`${testid}-none`}>
              <svg width="20" height="20" viewBox="0 0 20 20" className="absolute inset-0"><line x1="3" y1="17" x2="17" y2="3" stroke="#ef4444" strokeWidth="1.5" /></svg>
            </button>
          )}
          {Object.keys(COLOR).map(k => (
            <button key={k} onClick={() => onChange(COLOR[k].border)} title={k}
              className={`w-5 h-5 rounded-full hover:scale-110 transition-transform border ${value === COLOR[k].border ? 'ring-2 ring-offset-1 ring-indigo-500' : 'border-zinc-200'}`}
              style={{ background: COLOR[k].border }}
              data-testid={`${testid}-${k}`} />
          ))}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function WidthDropdown({ value = 2, onChange, label = 'Width', testid }) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button title={label} className="h-7 px-1.5 rounded hover:bg-zinc-100 inline-flex items-center gap-1 text-zinc-600 text-[11px]" data-testid={testid}>
          <div style={{ width: 14, height: value, background: '#52525b', borderRadius: 1 }} />
          <CaretDown size={9} />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="p-2 w-auto">
        <div className="text-[10px] uppercase tracking-wider font-bold text-zinc-400 mb-1.5 px-1">{label}</div>
        <div className="space-y-0.5">
          {BORDER_WIDTHS.map(w => (
            <button key={w} onClick={() => onChange(w)} className={`w-32 h-7 px-2 rounded text-[11px] flex items-center gap-2 hover:bg-zinc-100 ${value === w ? 'bg-indigo-50 text-indigo-700' : 'text-zinc-600'}`} data-testid={`${testid}-${w}`}>
              <div style={{ width: 18, height: w, background: 'currentColor', borderRadius: 1 }} />
              <span>{w} px</span>
            </button>
          ))}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function StyleDropdown({ value = 'solid', onChange, testid }) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button title="Line style" className="h-7 px-1.5 rounded hover:bg-zinc-100 inline-flex items-center gap-1 text-zinc-600 text-[11px] font-mono" data-testid={testid}>
          {BORDER_STYLES.find(s => s.id === value)?.preview}
          <CaretDown size={9} />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="p-1 w-auto">
        {BORDER_STYLES.map(s => (
          <button key={s.id} onClick={() => onChange(s.id)} className={`w-28 h-7 px-2 rounded text-[11px] font-mono text-left hover:bg-zinc-100 ${value === s.id ? 'bg-indigo-50 text-indigo-700' : 'text-zinc-600'}`} data-testid={`${testid}-${s.id}`}>{s.preview}  {s.id}</button>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function ArrowDropdown({ edge, setEdgeProp }) {
  const stroke = edge.style?.stroke || '#6366f1';
  const mk = (color) => ({ type: MarkerType.ArrowClosed, color, width: 18, height: 18 });
  const opts = [
    { id: 'none',  label: '─',  start: false,        end: false      , title: 'No arrows' },
    { id: 'end',   label: '→',  start: false,        end: mk(stroke) , title: 'End arrow' },
    { id: 'start', label: '←',  start: mk(stroke),   end: false      , title: 'Start arrow' },
    { id: 'both',  label: '↔',  start: mk(stroke),   end: mk(stroke) , title: 'Both arrows' },
  ];
  const current = (!edge.markerStart && !edge.markerEnd) ? 'none'
    : (!edge.markerStart && edge.markerEnd) ? 'end'
    : (edge.markerStart && !edge.markerEnd) ? 'start' : 'both';
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button title="Arrow direction" className="h-7 px-1.5 rounded hover:bg-zinc-100 inline-flex items-center gap-1 text-zinc-600 text-[13px]" data-testid="wb-fmt-arrows">
          {opts.find(o => o.id === current)?.label}
          <CaretDown size={9} />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="p-1 w-auto">
        {opts.map(o => (
          <button key={o.id} onClick={() => setEdgeProp(edge.id, { markerStart: o.start, markerEnd: o.end })}
            className={`w-32 h-7 px-2 rounded text-[12px] text-left hover:bg-zinc-100 ${current === o.id ? 'bg-indigo-50 text-indigo-700' : 'text-zinc-600'}`} data-testid={`wb-fmt-arrow-${o.id}`}>
            <span className="inline-block w-5 text-center text-[14px]">{o.label}</span> {o.title}
          </button>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// Compact Lucid-style format strip — designed to live INSIDE the top
// toolbar. Renders nothing (an inert placeholder) if no selection so the
// toolbar layout doesn't shift.
function FormatBar({ node, edge, setNodeProp, setEdgeProp, duplicate, removeNode, removeEdge }) {
  if (!node && !edge) {
    return <div className="text-[11px] text-zinc-400 italic px-2" data-testid="wb-format-bar-empty">Select a shape or connector to format it</div>;
  }
  if (node) {
    const d = node.data || {};
    const fontSize = d.fontSize || 12;
    const cycleFont = (delta) => {
      const idx = FONT_SIZES.indexOf(fontSize);
      const next = FONT_SIZES[Math.max(0, Math.min(FONT_SIZES.length - 1, (idx === -1 ? 1 : idx) + delta))];
      setNodeProp(node.id, { fontSize: next });
    };
    const Btn = ({ active, onClick, title, children, testid }) => (
      <button onClick={onClick} title={title}
        className={`h-7 w-7 rounded text-[12px] font-bold transition-colors flex items-center justify-center ${active ? 'bg-indigo-100 text-indigo-700' : 'text-zinc-700 hover:bg-zinc-100'}`}
        data-testid={testid}>{children}</button>
    );
    return (
      <div className="flex items-center gap-0.5 flex-nowrap" data-testid="wb-format-bar-node">
        <button onClick={() => cycleFont(-1)} title="Smaller text" className="w-7 h-7 rounded hover:bg-zinc-100 text-zinc-600 text-[11px] font-bold" data-testid="wb-fmt-font-smaller">A−</button>
        <span className="text-[11px] text-zinc-500 tabular-nums px-0.5 min-w-[20px] text-center">{fontSize}</span>
        <button onClick={() => cycleFont(+1)} title="Larger text" className="w-7 h-7 rounded hover:bg-zinc-100 text-zinc-600 text-[12px] font-bold" data-testid="wb-fmt-font-larger">A+</button>
        <Sep />
        <Btn active={!!d.bold}      onClick={() => setNodeProp(node.id, { bold: !d.bold })}        title="Bold (Ctrl+B)" testid="wb-fmt-bold"><span style={{ fontFamily: 'serif' }}>B</span></Btn>
        <Btn active={!!d.italic}    onClick={() => setNodeProp(node.id, { italic: !d.italic })}    title="Italic (Ctrl+I)" testid="wb-fmt-italic"><span style={{ fontStyle: 'italic', fontFamily: 'serif' }}>I</span></Btn>
        <Btn active={!!d.underline} onClick={() => setNodeProp(node.id, { underline: !d.underline })} title="Underline (Ctrl+U)" testid="wb-fmt-underline"><span style={{ textDecoration: 'underline', fontFamily: 'serif' }}>U</span></Btn>
        <Sep />
        <ColorDropdown value={d.textColor} onChange={(c) => setNodeProp(node.id, { textColor: c })} icon={<span className="text-[11px] font-bold">A</span>} title="Text" testid="wb-fmt-text-color" />
        <ColorDropdown value={d.fillColor || colorOf(d.color).bg} onChange={(c) => setNodeProp(node.id, { fillColor: c })} allowNone icon={<PaintBucket size={11} weight="bold" />} title="Fill" testid="wb-fmt-fill" />
        <ColorDropdown value={d.borderColor || colorOf(d.color).border} onChange={(c) => setNodeProp(node.id, { borderColor: c })} icon={<Square size={11} weight="bold" />} title="Border" testid="wb-fmt-border-color" />
        <WidthDropdown value={d.borderWidth || 2} onChange={(w) => setNodeProp(node.id, { borderWidth: w })} testid="wb-fmt-border-w" />
        <StyleDropdown value={d.borderStyle || 'solid'} onChange={(s) => setNodeProp(node.id, { borderStyle: s })} testid="wb-fmt-border-style" />
        <Sep />
        <button onClick={() => duplicate(node.id)} className="h-7 w-7 rounded hover:bg-zinc-100 text-zinc-600 flex items-center justify-center" title="Duplicate (Ctrl+D)" data-testid="wb-fmt-duplicate"><Copy size={12} /></button>
        <button onClick={() => removeNode(node.id)} className="h-7 w-7 rounded hover:bg-rose-50 text-rose-600 flex items-center justify-center" title="Delete (Del)" data-testid="wb-fmt-delete"><Trash size={12} /></button>
      </div>
    );
  }
  // ── Edge format controls ──
  const e = edge;
  const stroke = e.style?.stroke || '#6366f1';
  const strokeWidth = e.style?.strokeWidth || 2;
  const dash = e.style?.strokeDasharray ? (String(e.style.strokeDasharray).startsWith('2') ? 'dotted' : 'dashed') : 'solid';
  const setStyle = (patch) => setEdgeProp(e.id, { style: patch });
  return (
    <div className="flex items-center gap-0.5 flex-nowrap" data-testid="wb-format-bar-edge">
      <span className="text-[10px] uppercase tracking-wider font-bold text-zinc-400 mr-1">Connector</span>
      <ColorDropdown value={stroke} onChange={(c) => setStyle({ stroke: c })} icon={<Square size={11} weight="bold" />} title="Color" testid="wb-fmt-edge-color" />
      <WidthDropdown value={strokeWidth} onChange={(w) => setStyle({ strokeWidth: w })} label="Weight" testid="wb-fmt-edge-w" />
      <StyleDropdown value={dash} onChange={(s) => setStyle({ strokeDasharray: s === 'dashed' ? '8 4' : (s === 'dotted' ? '2 3' : undefined) })} testid="wb-fmt-edge-style" />
      <ArrowDropdown edge={e} setEdgeProp={setEdgeProp} />
      <Sep />
      <button onClick={() => removeEdge(e.id)} className="h-7 w-7 rounded hover:bg-rose-50 text-rose-600 flex items-center justify-center" title="Delete connector" data-testid="wb-fmt-edge-delete"><Trash size={12} /></button>
    </div>
  );
}
const Sep = () => <div className="w-px h-5 bg-zinc-200 mx-0.5" />;

// ─── Inline-editable label ───────────────────────────────────
// Click-to-edit (single click) → renders a textarea right inside the shape.
// Enter commits, Shift+Enter newline, Esc reverts. Blur also commits.
// Looks identical to the static label until the user enters edit mode, so the
// shape geometry doesn't shift around.
function NodeLabel({ id, data, color, fontSize = 12, weight = '600', italic = false, underline = false, alignTop = false }) {
  const fns = window.__wbNodeFns;
  const isEditing = !!data.__editing;
  const [val, setVal] = useState(data.label || '');
  const taRef = useRef(null);
  useEffect(() => { if (!isEditing) setVal(data.label || ''); }, [data.label, isEditing]);
  useEffect(() => {
    if (isEditing && taRef.current) {
      taRef.current.focus();
      taRef.current.select();
    }
  }, [isEditing]);

  const commit = useCallback(() => fns?.commitLabel?.(id, val), [fns, id, val]);
  const cancel = useCallback(() => fns?.commitLabel?.(id, data.label || ''), [fns, id, data.label]);

  const sharedStyle = {
    color, fontSize: `${fontSize}px`, fontWeight: weight,
    fontStyle: italic ? 'italic' : 'normal',
    textDecoration: underline ? 'underline' : 'none',
  };

  if (isEditing) {
    return (
      <div className={`absolute inset-0 flex ${alignTop ? 'items-start pt-3' : 'items-center'} justify-center px-2`}>
        <textarea
          ref={taRef}
          value={val}
          onChange={e => setVal(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            e.stopPropagation();
            if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); commit(); }
            else if (e.key === 'Escape') { e.preventDefault(); cancel(); }
          }}
          onMouseDown={e => e.stopPropagation()}
          onClick={e => e.stopPropagation()}
          className="w-full bg-transparent outline-none resize-none text-center leading-snug"
          style={{ ...sharedStyle, minHeight: '1.2em' }}
          data-testid={`wb-inline-edit-${id}`}
          rows={Math.max(1, (val || '').split('\n').length)}
          placeholder="Type…"
        />
      </div>
    );
  }
  return (
    <div className={`absolute inset-0 flex ${alignTop ? 'items-start pt-3' : 'items-center'} justify-center px-3 text-center pointer-events-none`}>
      <div className="leading-snug break-words whitespace-pre-wrap" style={sharedStyle}>
        {data.label || ''}
      </div>
    </div>
  );
}

function StickyNode({ data, selected, id, width, height }) {
  const c = colorOf(data.color || 'amber');
  const fill = data.fillColor || c.bg;
  const textColor = data.textColor || c.text;
  return (
    <div className="relative shadow-md" style={{
      background: fill, color: textColor,
      width: width || 160, height: height || 100, padding: 14,
      transform: 'rotate(-1deg)', borderRadius: 2,
      boxShadow: '0 2px 4px rgba(0,0,0,0.08)',
    }}>
      <NodeResizer minWidth={120} minHeight={80} isVisible={selected}
        lineStyle={{ borderColor: '#3b82f6', borderWidth: 1 }}
        handleStyle={{ width: 7, height: 7, background: '#fff', border: '1px solid #3b82f6', borderRadius: 1 }} />
      <NodeLabel id={id} data={data} color={textColor} fontSize={data.fontSize || 12} weight={data.bold ? '700' : '500'} italic={data.italic} underline={data.underline} alignTop />
      <FourHandles />
    </div>
  );
}

function TextNode({ data, selected, id, width, height }) {
  return (
    <div className="relative px-3 py-1" style={{
      width: width || undefined,
      height: height || undefined,
      minWidth: 80, minHeight: 32,
      borderRadius: 4,
    }}>
      <NodeResizer minWidth={80} minHeight={32} isVisible={selected}
        lineStyle={{ borderColor: '#3b82f6', borderWidth: 1, borderStyle: 'dashed' }}
        handleStyle={{ width: 7, height: 7, background: '#fff', border: '1px solid #3b82f6', borderRadius: 1 }} />
      <NodeLabel id={id} data={data} color={data.textColor || '#27272a'} fontSize={data.fontSize || 14} weight={data.bold ? '700' : '600'} italic={data.italic} underline={data.underline} />
      <FourHandles />
    </div>
  );
}

const NODE_TYPES = { shape: ShapeNode, sticky: StickyNode, text: TextNode };

// ─── Custom edge with inline Delete button ──────────────────────
function DeletableEdge({ id, sourceX, sourceY, targetX, targetY, sourcePosition, targetPosition, markerStart, markerEnd, style, selected, data }) {
  const [edgePath, labelX, labelY] = getSmoothStepPath({ sourceX, sourceY, sourcePosition, targetX, targetY, targetPosition, borderRadius: 8 });
  const onDelete = data?.__onDelete;
  return (
    <>
      <BaseEdge id={id} path={edgePath} markerStart={markerStart} markerEnd={markerEnd} style={{ ...style, strokeWidth: selected ? 3 : 2, stroke: selected ? '#4f46e5' : (style?.stroke || '#6366f1') }} />
      {data?.label && (
        <EdgeLabelRenderer>
          <div style={{ position: 'absolute', transform: `translate(-50%, -120%) translate(${labelX}px, ${labelY}px)`, pointerEvents: 'none' }}
            className="px-1.5 py-0.5 rounded bg-white border border-zinc-200 shadow-sm text-[10px] font-semibold text-zinc-700">
            {data.label}
          </div>
        </EdgeLabelRenderer>
      )}
      {selected && onDelete && (
        <EdgeLabelRenderer>
          <button
            onClick={(e) => { e.stopPropagation(); onDelete(id); }}
            style={{ position: 'absolute', transform: `translate(-50%, -50%) translate(${labelX}px, ${labelY}px)`, pointerEvents: 'all' }}
            className="w-5 h-5 rounded-full bg-rose-500 hover:bg-rose-600 text-white flex items-center justify-center shadow-md ring-2 ring-white"
            title="Delete connector"
            data-testid={`wb-edge-delete-${id}`}
          >
            <XIcon size={11} weight="bold" />
          </button>
        </EdgeLabelRenderer>
      )}
    </>
  );
}

const EDGE_TYPES = { deletable: DeletableEdge };

// ─── Categorized shape palette ─────────────────────────────────
// All shapes default to "white" so the canvas starts clean and the user can
// fill them via the floating toolbar — Lucid-style.
const SHAPE_LIBRARY = [
  {
    id: 'basic', label: 'Basic Shapes', items: [
      { name: 'Rectangle',     kind: 'shape', shape: 'rectangle',    color: 'white',  defaultLabel: '' },
      { name: 'Rounded',       kind: 'shape', shape: 'rounded',      color: 'white',  defaultLabel: '' },
      { name: 'Pill',          kind: 'shape', shape: 'pill',         color: 'white',  defaultLabel: '' },
      { name: 'Circle',        kind: 'shape', shape: 'circle',       color: 'white',  defaultLabel: '' },
      { name: 'Ellipse',       kind: 'shape', shape: 'ellipse',      color: 'white',  defaultLabel: '' },
      { name: 'Diamond',       kind: 'shape', shape: 'diamond',      color: 'white',  defaultLabel: '' },
      { name: 'Triangle',      kind: 'shape', shape: 'triangle',     color: 'white',  defaultLabel: '' },
      { name: 'Triangle Down', kind: 'shape', shape: 'triangleDown', color: 'white',  defaultLabel: '' },
      { name: 'Pentagon',      kind: 'shape', shape: 'pentagon',     color: 'white',  defaultLabel: '' },
      { name: 'Hexagon',       kind: 'shape', shape: 'hexagon',      color: 'white',  defaultLabel: '' },
      { name: 'Star',          kind: 'shape', shape: 'star',         color: 'white',  defaultLabel: '' },
      { name: 'Arrow',         kind: 'shape', shape: 'arrowRight',   color: 'white',  defaultLabel: '' },
      { name: 'Double Arrow',  kind: 'shape', shape: 'arrowDouble',  color: 'white',  defaultLabel: '' },
    ],
  },
  {
    id: 'flowchart', label: 'Flowchart', items: [
      { name: 'Process',       kind: 'shape', shape: 'rounded',       color: 'white', defaultLabel: 'Process' },
      { name: 'Decision',      kind: 'shape', shape: 'diamond',       color: 'white', defaultLabel: 'Decision?' },
      { name: 'Start / End',   kind: 'shape', shape: 'pill',          color: 'white', defaultLabel: 'Start' },
      { name: 'Document',      kind: 'shape', shape: 'document',      color: 'white', defaultLabel: 'Document' },
      { name: 'Manual Input',  kind: 'shape', shape: 'parallelogram', color: 'white', defaultLabel: 'Manual input' },
      { name: 'Display',       kind: 'shape', shape: 'trapezoid',     color: 'white', defaultLabel: 'Display' },
      { name: 'Connector',     kind: 'shape', shape: 'circle',        color: 'white', defaultLabel: 'A' },
      { name: 'Hexagon',       kind: 'shape', shape: 'hexagon',       color: 'white', defaultLabel: 'Prep' },
    ],
  },
  {
    id: 'arch', label: 'Architecture', items: [
      { name: 'Service',       kind: 'shape', shape: 'cloud',     color: 'white', defaultLabel: 'Service' },
      { name: 'Database',      kind: 'shape', shape: 'database',  color: 'white', defaultLabel: 'Database' },
      { name: 'Cylinder',      kind: 'shape', shape: 'cylinder',  color: 'white', defaultLabel: 'Storage' },
      { name: 'API',           kind: 'shape', shape: 'rounded',   color: 'white', defaultLabel: 'API' },
      { name: 'Queue',         kind: 'shape', shape: 'pill',      color: 'white', defaultLabel: 'Queue' },
      { name: 'User',          kind: 'shape', shape: 'circle',    color: 'white', defaultLabel: 'User' },
      { name: 'External',      kind: 'shape', shape: 'callout',   color: 'white', defaultLabel: 'External' },
    ],
  },
  {
    id: 'aws', label: 'AWS', items: [
      { name: 'EC2',         kind: 'shape', shape: 'rectangle', color: 'amber',  defaultLabel: 'EC2' },
      { name: 'S3',          kind: 'shape', shape: 'rectangle', color: 'emerald',defaultLabel: 'S3 Bucket' },
      { name: 'Lambda',      kind: 'shape', shape: 'rounded',   color: 'amber',  defaultLabel: 'λ Lambda' },
      { name: 'RDS',         kind: 'shape', shape: 'database',  color: 'sky',    defaultLabel: 'RDS' },
      { name: 'DynamoDB',    kind: 'shape', shape: 'database',  color: 'sky',    defaultLabel: 'DynamoDB' },
      { name: 'SQS',         kind: 'shape', shape: 'pill',      color: 'rose',   defaultLabel: 'SQS Queue' },
      { name: 'SNS',         kind: 'shape', shape: 'pill',      color: 'rose',   defaultLabel: 'SNS Topic' },
      { name: 'API Gateway', kind: 'shape', shape: 'rounded',   color: 'violet', defaultLabel: 'API Gateway' },
      { name: 'CloudFront',  kind: 'shape', shape: 'cloud',     color: 'violet', defaultLabel: 'CloudFront' },
      { name: 'Route 53',    kind: 'shape', shape: 'circle',    color: 'violet', defaultLabel: 'Route 53' },
    ],
  },
  {
    id: 'azure', label: 'Azure', items: [
      { name: 'VM',           kind: 'shape', shape: 'rectangle', color: 'sky',    defaultLabel: 'VM' },
      { name: 'Functions',    kind: 'shape', shape: 'rounded',   color: 'sky',    defaultLabel: 'ƒ Functions' },
      { name: 'SQL DB',       kind: 'shape', shape: 'database',  color: 'sky',    defaultLabel: 'SQL Database' },
      { name: 'Cosmos DB',    kind: 'shape', shape: 'database',  color: 'violet', defaultLabel: 'Cosmos DB' },
      { name: 'Storage',      kind: 'shape', shape: 'cylinder',  color: 'sky',    defaultLabel: 'Blob Storage' },
      { name: 'Service Bus',  kind: 'shape', shape: 'pill',      color: 'sky',    defaultLabel: 'Service Bus' },
      { name: 'App Service',  kind: 'shape', shape: 'rounded',   color: 'sky',    defaultLabel: 'App Service' },
      { name: 'AKS',          kind: 'shape', shape: 'hexagon',   color: 'sky',    defaultLabel: 'AKS' },
    ],
  },
  {
    id: 'gcp', label: 'GCP', items: [
      { name: 'GCE',         kind: 'shape', shape: 'rectangle', color: 'rose',    defaultLabel: 'Compute Eng.' },
      { name: 'Cloud Run',   kind: 'shape', shape: 'rounded',   color: 'sky',     defaultLabel: 'Cloud Run' },
      { name: 'GKE',         kind: 'shape', shape: 'hexagon',   color: 'sky',     defaultLabel: 'GKE' },
      { name: 'Cloud Storage', kind: 'shape', shape: 'cylinder',color: 'amber',   defaultLabel: 'Cloud Storage' },
      { name: 'BigQuery',    kind: 'shape', shape: 'database',  color: 'sky',     defaultLabel: 'BigQuery' },
      { name: 'Pub/Sub',     kind: 'shape', shape: 'pill',      color: 'emerald', defaultLabel: 'Pub/Sub' },
      { name: 'Cloud Func.', kind: 'shape', shape: 'rounded',   color: 'amber',   defaultLabel: 'Cloud Function' },
    ],
  },
  {
    id: 'uml', label: 'UML', items: [
      { name: 'Class',       kind: 'shape', shape: 'rectangle', color: 'white', defaultLabel: 'ClassName\n──────\n+ field\n+ method()' },
      { name: 'Interface',   kind: 'shape', shape: 'rectangle', color: 'white', defaultLabel: '«interface»\nName' },
      { name: 'Actor',       kind: 'shape', shape: 'circle',    color: 'white', defaultLabel: 'Actor' },
      { name: 'Use Case',    kind: 'shape', shape: 'ellipse',   color: 'white', defaultLabel: 'Use Case' },
      { name: 'Component',   kind: 'shape', shape: 'rectangle', color: 'white', defaultLabel: '«component»\nName' },
      { name: 'Package',     kind: 'shape', shape: 'rectangle', color: 'white', defaultLabel: '«package»\nName' },
      { name: 'Note',        kind: 'shape', shape: 'document',  color: 'white', defaultLabel: 'note' },
    ],
  },
  {
    id: 'erd', label: 'ERD', items: [
      { name: 'Entity',          kind: 'shape', shape: 'rectangle', color: 'white', defaultLabel: 'Entity' },
      { name: 'Strong Entity',   kind: 'shape', shape: 'rectangle', color: 'sky',   defaultLabel: 'User' },
      { name: 'Weak Entity',     kind: 'shape', shape: 'rectangle', color: 'rose',  defaultLabel: 'OrderItem' },
      { name: 'Relationship',    kind: 'shape', shape: 'diamond',   color: 'amber', defaultLabel: 'has' },
      { name: 'Attribute',       kind: 'shape', shape: 'ellipse',   color: 'white', defaultLabel: 'attribute' },
      { name: 'Key Attribute',   kind: 'shape', shape: 'ellipse',   color: 'emerald',defaultLabel: 'id (PK)' },
      { name: 'Multivalued',     kind: 'shape', shape: 'ellipse',   color: 'violet',defaultLabel: 'tags[]' },
    ],
  },
  {
    id: 'notes', label: 'Notes & Text', items: [
      { name: 'Sticky Yellow', kind: 'sticky', color: 'amber',   defaultLabel: 'Note…' },
      { name: 'Sticky Pink',   kind: 'sticky', color: 'rose',    defaultLabel: 'Note…' },
      { name: 'Sticky Green',  kind: 'sticky', color: 'emerald', defaultLabel: 'Note…' },
      { name: 'Sticky Blue',   kind: 'sticky', color: 'sky',     defaultLabel: 'Note…' },
      { name: 'Text',          kind: 'text',   color: 'slate',   defaultLabel: 'Text' },
    ],
  },
];

// Tiny preview SVG for the palette tile.
function ShapePreview({ item }) {
  if (item.kind === 'sticky') {
    const c = colorOf(item.color);
    return <div className="w-7 h-6 shadow-sm" style={{ background: c.bg, transform: 'rotate(-2deg)', borderRadius: 1 }} />;
  }
  if (item.kind === 'text') {
    return <span className="text-[14px] font-bold text-zinc-700">T</span>;
  }
  const g = SHAPE_GEOMETRY[item.shape] || SHAPE_GEOMETRY.rectangle;
  const c = colorOf(item.color);
  // Scale into a 32x24 preview
  return (
    <svg viewBox={`0 0 ${g.w} ${g.h}`} className="w-8 h-6" style={{ overflow: 'visible' }}
      dangerouslySetInnerHTML={{ __html: g.draw(c) }} />
  );
}

// ─── Templates (kept simple; rebuilt to use shape kind) ─────────
const tEdge = (id, source, target, sH, tH, label) => ({
  id, source, target, sourceHandle: sH, targetHandle: tH,
  type: 'deletable',
  data: label ? { label } : {},
  markerEnd: { type: MarkerType.ArrowClosed, color: '#6366f1', width: 18, height: 18 },
  style: { stroke: '#6366f1', strokeWidth: 2 },
});
const tNode = (id, shape, color, x, y, label) => ({ id, type: 'shape', position: { x, y }, data: { shape, color, label } });
const TEMPLATES = {
  flowchart: {
    name: 'Flowchart',
    nodes: [
      tNode('n1', 'pill',     'emerald', 250,  50,  'Start'),
      tNode('n2', 'rounded',  'indigo',  240,  200, 'Process A'),
      tNode('n3', 'diamond',  'amber',   240,  320, 'Valid?'),
      tNode('n4', 'rounded',  'rose',    50,   500, 'Handle error'),
      tNode('n5', 'rounded',  'indigo',  440,  500, 'Continue'),
      tNode('n6', 'pill',     'emerald', 470,  660, 'End'),
    ],
    edges: [
      tEdge('e1', 'n1', 'n2', 'b', 't'),
      tEdge('e2', 'n2', 'n3', 'b', 't'),
      tEdge('e3', 'n3', 'n4', 'l', 't', 'No'),
      tEdge('e4', 'n3', 'n5', 'r', 't', 'Yes'),
      tEdge('e5', 'n5', 'n6', 'b', 't'),
    ],
  },
  architecture: {
    name: 'System Architecture',
    nodes: [
      tNode('a1', 'cloud',   'violet',  60,   80,  'Web App'),
      tNode('a2', 'cloud',   'violet',  60,   240, 'Mobile App'),
      tNode('a3', 'rounded', 'indigo',  320,  160, 'API Gateway'),
      tNode('a4', 'rounded', 'indigo',  580,  60,  'Auth Service'),
      tNode('a5', 'rounded', 'indigo',  580,  180, 'Core Service'),
      tNode('a6', 'rounded', 'indigo',  580,  300, 'Notify Service'),
      tNode('a7', 'database','sky',     860,  180, 'PostgreSQL'),
    ],
    edges: [
      tEdge('ea1', 'a1', 'a3', 'r', 'l'),
      tEdge('ea2', 'a2', 'a3', 'r', 'l'),
      tEdge('ea3', 'a3', 'a4', 'r', 'l'),
      tEdge('ea4', 'a3', 'a5', 'r', 'l'),
      tEdge('ea5', 'a3', 'a6', 'r', 'l'),
      tEdge('ea6', 'a5', 'a7', 'r', 'l'),
    ],
  },
  retro: {
    name: 'Sprint Retro',
    nodes: [
      { id: 'r1', type: 'sticky', position: { x: 60,  y: 60 },  data: { label: 'What went WELL', color: 'emerald' } },
      { id: 'r2', type: 'sticky', position: { x: 320, y: 60 },  data: { label: "What didn't",     color: 'rose' } },
      { id: 'r3', type: 'sticky', position: { x: 580, y: 60 },  data: { label: 'Action items',    color: 'amber' } },
      { id: 'r4', type: 'sticky', position: { x: 60,  y: 220 }, data: { label: '• Write something here', color: 'emerald' } },
      { id: 'r5', type: 'sticky', position: { x: 320, y: 220 }, data: { label: '• Write something here', color: 'rose' } },
      { id: 'r6', type: 'sticky', position: { x: 580, y: 220 }, data: { label: '• Write something here', color: 'amber' } },
    ],
    edges: [],
  },
  blank: { name: 'Blank canvas', nodes: [], edges: [] },
};

// ─── Inline label dialog ────────────────────────────────────────
function InlineLabelDialog({ open, value, onChange, onClose }) {
  const [val, setVal] = useState(value || '');
  useEffect(() => { setVal(value || ''); }, [value]);
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[1000] bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl p-5 w-full max-w-md" onClick={e => e.stopPropagation()}>
        <div className="text-[13px] font-bold text-zinc-700 mb-3">Edit label</div>
        <textarea autoFocus value={val} onChange={e => setVal(e.target.value)} rows={3}
          className="w-full px-3 py-2 text-[13px] border border-zinc-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
          onKeyDown={e => {
            if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); onChange(val); onClose(); }
            else if (e.key === 'Escape') onClose();
          }}
          data-testid="wb-label-input" />
        <div className="flex justify-end gap-2 mt-3">
          <Button variant="ghost" size="sm" onClick={onClose}>Cancel</Button>
          <Button size="sm" className="bg-indigo-600 hover:bg-indigo-700" onClick={() => { onChange(val); onClose(); }} data-testid="wb-label-save">Save</Button>
        </div>
      </div>
    </div>
  );
}

export default function WhiteboardView({ projectId, canEdit }) {
  return (
    <ReactFlowProvider>
      <WhiteboardInner projectId={projectId} canEdit={canEdit} />
    </ReactFlowProvider>
  );
}

function WhiteboardInner({ projectId, canEdit }) {
  const { getOrgHeaders, API } = useAuth();
  const navigate = useNavigate();
  const confirm = useConfirm();
  const flowWrapper = useRef(null);
  const rfInstance = useRef(null);

  const [loading, setLoading] = useState(true);
  const [gated, setGated] = useState(false);
  const [savingState, setSavingState] = useState('idle');
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const [boardName, setBoardName] = useState('Project whiteboard');
  // Multi-board state — list of boards on this project + currently-open board id.
  // Free orgs see no boards (gated); Business orgs get up to MAX_BOARDS_BUSINESS (5).
  const [boards, setBoards] = useState([]);
  const [currentBoardId, setCurrentBoardId] = useState(null);
  const [maxBoards, setMaxBoards] = useState(5);
  // Inline edit: which node id is currently in edit mode (null = none).
  // Edge labels still use a small dialog since edges have no inline space.
  const [editingNodeId, setEditingNodeId] = useState(null);
  const [editingEdge, setEditingEdge] = useState(null);
  const [showTemplates, setShowTemplates] = useState(false);
  const [paletteSearch, setPaletteSearch] = useState('');
  const [collapsedGroups, setCollapsedGroups] = useState({});
  const [palCollapsed, setPalCollapsed] = useState(() => {
    try { return localStorage.getItem('iw_wb_palette_collapsed') === '1'; } catch { return false; }
  });
  const togglePalette = () => setPalCollapsed(v => {
    const n = !v;
    try { localStorage.setItem('iw_wb_palette_collapsed', n ? '1' : '0'); } catch {}
    return n;
  });

  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);

  // ─── Undo / redo history ────────────────────────────────────
  // Snapshot taken BEFORE each mutating action; user pressing Ctrl+Z pops
  // past → applies → pushes current to future. Shift+Z (or Ctrl+Y) reverses.
  const historyRef = useRef({ past: [], future: [] });
  const snapshot = useCallback((n, e) => JSON.stringify({
    nodes: n,
    edges: e.map(({ data, ...r }) => ({ ...r, data: data ? Object.fromEntries(Object.entries(data).filter(([k]) => !k.startsWith('__'))) : {} })),
  }), []);
  const recordHistory = useCallback(() => {
    const cur = snapshot(nodes, edges);
    historyRef.current.past.push(cur);
    if (historyRef.current.past.length > 50) historyRef.current.past.shift();
    historyRef.current.future = [];
  }, [nodes, edges, snapshot]);
  const applySnap = useCallback((s) => {
    const obj = JSON.parse(s);
    setNodes(obj.nodes || []);
    setEdges((obj.edges || []).map(e => ({ ...e, type: e.type || 'deletable' })));
  }, [setNodes, setEdges]);
  const undo = useCallback(() => {
    if (!historyRef.current.past.length) return;
    historyRef.current.future.push(snapshot(nodes, edges));
    applySnap(historyRef.current.past.pop());
  }, [nodes, edges, snapshot, applySnap]);
  const redo = useCallback(() => {
    if (!historyRef.current.future.length) return;
    historyRef.current.past.push(snapshot(nodes, edges));
    applySnap(historyRef.current.future.pop());
  }, [nodes, edges, snapshot, applySnap]);

  const headers = useCallback(() => ({ withCredentials: true, headers: getOrgHeaders() }), [getOrgHeaders]);
  const saveTimerRef = useRef(null);
  const lastSerializedRef = useRef('');
  const initializedRef = useRef(false);

  const deleteEdge = useCallback((edgeId) => {
    recordHistory();
    setEdges(es => es.filter(e => e.id !== edgeId));
  }, [setEdges, recordHistory]);

  // Inject onDelete into every edge so the custom edge component can call it.
  const edgesWithCallbacks = useMemo(
    () => edges.map(e => ({ ...e, type: e.type || 'deletable', data: { ...(e.data || {}), __onDelete: deleteEdge } })),
    [edges, deleteEdge]
  );

  // Inject runtime flags into nodes (similar to edgesWithCallbacks).
  // The __editing flag tells NodeLabel to render the inline textarea.
  const nodesWithEditing = useMemo(
    () => nodes.map(n => ({ ...n, data: { ...n.data, __editing: n.id === editingNodeId } })),
    [nodes, editingNodeId]
  );

  // ─── Compute the currently-selected node / edge for FormatBar ──
  const selectedNode = useMemo(() => nodes.find(n => n.selected) || null, [nodes]);
  const selectedEdge = useMemo(() => edges.find(e => e.selected) || null, [edges]);

  // ─── Load board list + open the first board ─────────────────
  const loadBoards = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/projects/${projectId}/whiteboards`, headers());
      setGated(!!data.gated);
      setMaxBoards(data.max_boards || 5);
      setBoards(data.boards || []);
      if (data.gated) { setLoading(false); return null; }
      if (!data.boards?.length) {
        // Auto-create a first board so the user lands on a working canvas.
        const { data: created } = await axios.post(`${API}/projects/${projectId}/whiteboards`, { name: 'Main board' }, headers());
        setBoards([created.board]);
        return created.board.board_id;
      }
      return data.boards[0].board_id;
    } catch (err) { console.error(err); setLoading(false); return null; }
  }, [projectId, API, headers]);

  const loadBoard = useCallback(async (boardId) => {
    if (!boardId) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/projects/${projectId}/whiteboards/${boardId}`, headers());
      const wb = data.whiteboard;
      if (wb) {
        setNodes(wb.nodes || []);
        setEdges((wb.edges || []).map(e => ({ ...e, type: e.type || 'deletable' })));
        setBoardName(wb.name || 'Untitled board');
        setLastSavedAt(wb.updated_at);
      } else {
        setNodes([]); setEdges([]);
      }
      lastSerializedRef.current = '';
      setTimeout(() => { initializedRef.current = true; }, 100);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [projectId, API, headers, setNodes, setEdges]);

  // First mount → list boards → load the first one.
  useEffect(() => {
    let cancelled = false;
    initializedRef.current = false;
    (async () => {
      const firstId = await loadBoards();
      if (cancelled) return;
      if (firstId) { setCurrentBoardId(firstId); await loadBoard(firstId); }
    })();
    return () => { cancelled = true; };
  }, [projectId, loadBoards, loadBoard]);

  // Switch board.
  const switchBoard = useCallback(async (boardId) => {
    if (boardId === currentBoardId) return;
    initializedRef.current = false;
    setCurrentBoardId(boardId);
    await loadBoard(boardId);
  }, [currentBoardId, loadBoard]);

  const createBoard = useCallback(async () => {
    if (boards.length >= maxBoards) { toast.error(`Business plan supports up to ${maxBoards} boards per project. Delete one to add another.`); return; }
    try {
      const { data } = await axios.post(`${API}/projects/${projectId}/whiteboards`, { name: `Board ${boards.length + 1}` }, headers());
      setBoards(b => [...b, data.board]);
      await switchBoard(data.board.board_id);
      toast.success('New board created');
    } catch (err) {
      const msg = err.response?.data?.detail || 'Failed to create board';
      toast.error(msg);
    }
  }, [boards, maxBoards, API, projectId, headers, switchBoard]);

  const deleteBoard = useCallback(async (boardId) => {
    if (boards.length <= 1) { toast.error("Can't delete the last board — create another one first"); return; }
    if (!(await confirm({
      title: 'Delete this board?',
      description: 'All shapes and connectors on this board will be permanently removed.',
      confirmText: 'Delete board', variant: 'destructive',
    }))) return;
    try {
      await axios.delete(`${API}/projects/${projectId}/whiteboards/${boardId}`, headers());
      const remaining = boards.filter(b => b.board_id !== boardId);
      setBoards(remaining);
      if (currentBoardId === boardId) {
        await switchBoard(remaining[0].board_id);
      }
      toast.success('Board deleted');
    } catch (err) { toast.error('Failed to delete board'); }
  }, [boards, confirm, API, projectId, headers, currentBoardId, switchBoard]);

  // Auto-save — now scoped to the current board.
  useEffect(() => {
    if (!initializedRef.current || gated || !canEdit || !currentBoardId) return;
    // Strip the runtime callback before serializing
    const cleanEdges = edges.map(({ data, ...rest }) => ({ ...rest, data: data ? Object.fromEntries(Object.entries(data).filter(([k]) => !k.startsWith('__'))) : {} }));
    const serialized = JSON.stringify({ nodes, edges: cleanEdges, name: boardName });
    if (serialized === lastSerializedRef.current) return;
    lastSerializedRef.current = serialized;
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    setSavingState('saving');
    saveTimerRef.current = setTimeout(async () => {
      try {
        const viewport = rfInstance.current?.getViewport?.() || null;
        await axios.put(`${API}/projects/${projectId}/whiteboards/${currentBoardId}`, {
          nodes, edges: cleanEdges, viewport, name: boardName, format: 'reactflow',
        }, headers());
        setSavingState('saved');
        setLastSavedAt(new Date().toISOString());
        // Reflect updated name + count in the boards list immediately.
        setBoards(b => b.map(x => x.board_id === currentBoardId ? { ...x, name: boardName, element_count: nodes.length + edges.length, updated_at: new Date().toISOString() } : x));
      } catch (err) { console.error(err); setSavingState('error'); }
    }, 1500);
  }, [nodes, edges, boardName, API, projectId, headers, gated, canEdit, currentBoardId]);

  useEffect(() => () => { if (saveTimerRef.current) clearTimeout(saveTimerRef.current); }, []);

  // Suppress the harmless `ResizeObserver loop completed with undelivered
  // notifications` warning that CRA's dev overlay otherwise paints over the UI.
  // See https://github.com/WICG/resize-observer/issues/38 — it's safe to ignore.
  useEffect(() => {
    const handler = (e) => {
      const msg = e?.message || e?.reason?.message || '';
      if (/ResizeObserver loop/.test(msg)) {
        e.stopImmediatePropagation?.();
        e.preventDefault?.();
      }
    };
    window.addEventListener('error', handler, { capture: true });
    window.addEventListener('unhandledrejection', handler, { capture: true });
    return () => {
      window.removeEventListener('error', handler, { capture: true });
      window.removeEventListener('unhandledrejection', handler, { capture: true });
    };
  }, []);

  const onConnect = useCallback((params) => {
    recordHistory();
    setEdges(eds => addEdge({
      ...params,
      type: 'deletable',
      data: {},
      markerEnd: { type: MarkerType.ArrowClosed, color: '#6366f1', width: 18, height: 18 },
      style: { stroke: '#6366f1', strokeWidth: 2 },
    }, eds));
  }, [setEdges, recordHistory]);

  const addNode = useCallback((item, atPos = null) => {
    if (!canEdit) return;
    recordHistory();
    const id = `n_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 6)}`;
    const center = atPos || rfInstance.current?.screenToFlowPosition?.({
      x: (flowWrapper.current?.clientWidth || 800) / 2,
      y: (flowWrapper.current?.clientHeight || 600) / 2,
    }) || { x: 200, y: 200 };
    const data = item.kind === 'shape'
      ? { shape: item.shape, color: item.color, label: item.defaultLabel || '' }
      : { color: item.color, label: item.defaultLabel || '' };
    setNodes(nds => nds.concat({ id, type: item.kind, position: { x: center.x - 70, y: center.y - 30 }, data, selected: true }));
    // Open inline editor immediately so the user just types the label.
    setEditingNodeId(id);
  }, [setNodes, canEdit, recordHistory]);

  // ─── Per-node mutations exposed to NodeToolbar ───────────────
  const removeNodeById = useCallback((nodeId) => {
    recordHistory();
    setNodes(nds => nds.filter(n => n.id !== nodeId));
    setEdges(es => es.filter(e => e.source !== nodeId && e.target !== nodeId));
  }, [setNodes, setEdges, recordHistory]);

  const duplicateNode = useCallback((nodeId) => {
    if (!canEdit) return;
    const orig = nodes.find(n => n.id === nodeId);
    if (!orig) return;
    recordHistory();
    const id = `n_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 6)}`;
    setNodes(nds => nds.concat({
      ...orig, id,
      position: { x: orig.position.x + 30, y: orig.position.y + 30 },
      selected: false,
      data: { ...orig.data },
    }));
  }, [nodes, setNodes, canEdit, recordHistory]);

  const setNodeColor = useCallback((nodeId, color) => {
    recordHistory();
    setNodes(nds => nds.map(n => n.id === nodeId ? { ...n, data: { ...n.data, color } } : n));
  }, [setNodes, recordHistory]);

  // Patch any single node-data property (used by FormatBar for fill/border/font/etc).
  const setNodeProp = useCallback((nodeId, patch) => {
    recordHistory();
    setNodes(nds => nds.map(n => n.id === nodeId ? { ...n, data: { ...n.data, ...patch } } : n));
  }, [setNodes, recordHistory]);

  // Patch a single edge property (used by EdgeFormatBar).
  const setEdgeProp = useCallback((edgeId, patch) => {
    recordHistory();
    setEdges(es => es.map(e => {
      if (e.id !== edgeId) return e;
      const next = { ...e, ...patch };
      // If style or marker patches arrived, merge them with existing.
      if (patch.style) next.style = { ...(e.style || {}), ...patch.style };
      if (patch.markerEnd === false) next.markerEnd = undefined;
      if (patch.markerStart === false) next.markerStart = undefined;
      return next;
    }));
  }, [setEdges, recordHistory]);

  // Inline label commit — called by NodeLabel on blur / Enter.
  const commitNodeLabel = useCallback((nodeId, newLabel) => {
    setNodes(nds => nds.map(n => n.id === nodeId ? { ...n, data: { ...n.data, label: newLabel } } : n));
    setEditingNodeId(null);
    // Snapshot AFTER the label is set so undo restores the prior label.
    setTimeout(() => recordHistory(), 0);
  }, [setNodes, recordHistory]);

  // Expose mutators to NodeToolbar / NodeLabel via window (NodeToolbar can't
  // easily get them via context, and the inline label sits inside the node tree).
  useEffect(() => {
    window.__wbNodeFns = {
      setColor: setNodeColor,
      duplicate: duplicateNode,
      removeNode: removeNodeById,
      commitLabel: commitNodeLabel,
      startEdit: (id) => setEditingNodeId(id),
    };
    return () => { delete window.__wbNodeFns; };
  }, [setNodeColor, duplicateNode, removeNodeById, commitNodeLabel]);

  // Snapshot AFTER a node-drag completes (before next interaction).
  const onNodeDragStart = useCallback(() => { recordHistory(); }, [recordHistory]);

  // Internal clipboard (we don't use the OS clipboard so paste works without
  // permission prompts and ignores text from other apps).
  const clipboardRef = useRef([]);

  const copySelection = useCallback(() => {
    const sel = nodes.filter(n => n.selected);
    if (!sel.length) return;
    clipboardRef.current = sel.map(n => ({ ...n, data: { ...n.data } }));
    toast.success(`Copied ${sel.length} shape${sel.length > 1 ? 's' : ''}`);
  }, [nodes]);

  const pasteClipboard = useCallback(() => {
    if (!canEdit) return;
    const items = clipboardRef.current;
    if (!items?.length) return;
    recordHistory();
    const idMap = {};
    const stamp = Date.now().toString(36);
    const cloned = items.map((n, i) => {
      const newId = `n_${stamp}_${i}_${Math.random().toString(36).slice(2, 5)}`;
      idMap[n.id] = newId;
      return {
        ...n,
        id: newId,
        position: { x: (n.position?.x || 0) + 30, y: (n.position?.y || 0) + 30 },
        selected: true,
        data: { ...n.data },
      };
    });
    // Deselect existing nodes so the pasted batch becomes the new selection.
    setNodes(nds => nds.map(n => ({ ...n, selected: false })).concat(cloned));
    toast.success(`Pasted ${cloned.length} shape${cloned.length > 1 ? 's' : ''}`);
  }, [setNodes, canEdit, recordHistory]);

  // Keyboard shortcuts: Ctrl/Cmd+Z (undo), Ctrl/Cmd+Shift+Z or Ctrl+Y (redo),
  // Ctrl/Cmd+D (duplicate), Ctrl/Cmd+C (copy selection), Ctrl/Cmd+V (paste).
  // Skip when typing in inputs.
  useEffect(() => {
    const handler = (e) => {
      const tag = (e.target?.tagName || '').toLowerCase();
      if (tag === 'input' || tag === 'textarea' || e.target?.isContentEditable) return;
      const mod = e.ctrlKey || e.metaKey;
      if (!mod) return;
      if (e.key === 'z' && !e.shiftKey) { e.preventDefault(); undo(); }
      else if ((e.key === 'y') || (e.key === 'z' && e.shiftKey)) { e.preventDefault(); redo(); }
      else if (e.key === 'd') {
        e.preventDefault();
        const sel = nodes.find(n => n.selected);
        if (sel) duplicateNode(sel.id);
      }
      else if (e.key === 'c') { e.preventDefault(); copySelection(); }
      else if (e.key === 'v') { e.preventDefault(); pasteClipboard(); }
      else if (e.key === 'b' && selectedNode) { e.preventDefault(); setNodeProp(selectedNode.id, { bold: !selectedNode.data?.bold }); }
      else if (e.key === 'i' && selectedNode) { e.preventDefault(); setNodeProp(selectedNode.id, { italic: !selectedNode.data?.italic }); }
      else if (e.key === 'u' && selectedNode) { e.preventDefault(); setNodeProp(selectedNode.id, { underline: !selectedNode.data?.underline }); }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [undo, redo, duplicateNode, copySelection, pasteClipboard, nodes, selectedNode, setNodeProp]);

  const onDragStart = (event, item) => {
    event.dataTransfer.setData('application/wb-item', JSON.stringify(item));
    event.dataTransfer.effectAllowed = 'move';
  };
  const onDragOver = useCallback((event) => { event.preventDefault(); event.dataTransfer.dropEffect = 'move'; }, []);
  const onDrop = useCallback((event) => {
    event.preventDefault();
    if (!canEdit) return;
    const raw = event.dataTransfer.getData('application/wb-item');
    if (!raw) return;
    const item = JSON.parse(raw);
    const pos = rfInstance.current.screenToFlowPosition({ x: event.clientX, y: event.clientY });
    addNode(item, pos);
  }, [addNode, canEdit]);

  const onNodeDoubleClick = useCallback((_e, node) => {
    if (!canEdit) return;
    setEditingNodeId(node.id);
  }, [canEdit]);

  const onEdgeDoubleClick = useCallback((_e, edge) => {
    if (!canEdit) return;
    setEditingEdge({ id: edge.id, value: edge.data?.label || '' });
  }, [canEdit]);

  const applyEdgeLabel = useCallback((newLabel) => {
    if (!editingEdge) return;
    recordHistory();
    setEdges(es => es.map(e => e.id === editingEdge.id ? { ...e, data: { ...(e.data || {}), label: newLabel } } : e));
  }, [editingEdge, setEdges, recordHistory]);

  const loadTemplate = useCallback((key) => {
    const t = TEMPLATES[key];
    if (!t) return;
    recordHistory();
    setNodes(t.nodes.map(n => ({ ...n })));
    setEdges(t.edges.map(e => ({ ...e })));
    setShowTemplates(false);
    setTimeout(() => rfInstance.current?.fitView?.({ padding: 0.2, duration: 400 }), 100);
  }, [setNodes, setEdges, recordHistory]);

  const clearBoard = useCallback(async () => {
    if (!(await confirm({
      title: 'Clear whiteboard?',
      description: 'This will delete every shape and connector. You cannot undo it.',
      confirmText: 'Yes, clear board', variant: 'destructive',
    }))) return;
    recordHistory();
    setNodes([]); setEdges([]);
    toast.success('Whiteboard cleared');
  }, [confirm, setNodes, setEdges, recordHistory]);

  const generateBoardImage = useCallback(async () => {
    const nodesBounds = getNodesBounds(nodes);
    const viewportEl = document.querySelector('.react-flow__viewport');
    if (!viewportEl) return null;
    const transform = getViewportForBounds(nodesBounds, 1600, 1100, 0.5, 2, 0.1);
    return toPng(viewportEl, {
      backgroundColor: '#ffffff', width: 1600, height: 1100,
      style: { width: 1600, height: 1100, transform: `translate(${transform.x}px, ${transform.y}px) scale(${transform.zoom})` },
    });
  }, [nodes]);

  const exportPng = useCallback(async () => {
    if (!nodes.length) { toast.error('Add some shapes first'); return; }
    try {
      const dataUrl = await generateBoardImage();
      if (!dataUrl) return;
      const a = document.createElement('a'); a.download = `${boardName || 'whiteboard'}.png`; a.href = dataUrl; a.click();
      toast.success('Exported PNG');
    } catch (err) { console.error(err); toast.error('Export failed'); }
  }, [nodes, boardName, generateBoardImage]);

  const exportPdf = useCallback(async () => {
    if (!nodes.length) { toast.error('Add some shapes first'); return; }
    try {
      const dataUrl = await generateBoardImage();
      if (!dataUrl) return;
      const pdf = new jsPDF({ orientation: 'landscape', unit: 'pt', format: [1600, 1100] });
      pdf.addImage(dataUrl, 'PNG', 0, 0, 1600, 1100);
      pdf.save(`${boardName || 'whiteboard'}.pdf`);
      toast.success('Exported PDF');
    } catch (err) { console.error(err); toast.error('Export failed'); }
  }, [nodes, boardName, generateBoardImage]);

  const exportJson = useCallback(() => {
    const cleanEdges = edges.map(({ data, ...rest }) => ({ ...rest, data: data ? Object.fromEntries(Object.entries(data).filter(([k]) => !k.startsWith('__'))) : {} }));
    const blob = new Blob([JSON.stringify({ name: boardName, nodes, edges: cleanEdges }, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = `${boardName || 'whiteboard'}.json`; a.click();
    URL.revokeObjectURL(url);
    toast.success('Exported JSON');
  }, [nodes, edges, boardName]);

  const fitView = useCallback(() => rfInstance.current?.fitView?.({ padding: 0.2, duration: 400 }), []);

  const defaultEdgeOptions = useMemo(() => ({
    type: 'deletable', data: {},
    markerEnd: { type: MarkerType.ArrowClosed, color: '#6366f1', width: 18, height: 18 },
    style: { stroke: '#6366f1', strokeWidth: 2 },
  }), []);

  // ─── Filtered palette ───────────────────────────────────────
  const filteredLibrary = useMemo(() => {
    if (!paletteSearch.trim()) return SHAPE_LIBRARY;
    const q = paletteSearch.toLowerCase();
    return SHAPE_LIBRARY.map(g => ({ ...g, items: g.items.filter(i => i.name.toLowerCase().includes(q) || (i.shape || '').toLowerCase().includes(q)) }))
      .filter(g => g.items.length > 0);
  }, [paletteSearch]);

  if (gated) {
    return (
      <div className="flex-1 h-full flex items-center justify-center bg-zinc-50/30 p-8" data-testid="whiteboard-locked">
        <div className="max-w-md text-center">
          <div className="w-16 h-16 mx-auto rounded-2xl bg-gradient-to-br from-violet-100 to-fuchsia-100 flex items-center justify-center mb-4">
            <Lock size={28} className="text-violet-600" weight="duotone" />
          </div>
          <h3 className="font-['Outfit'] text-[22px] font-bold text-zinc-900 mb-2">Whiteboards are a Business feature</h3>
          <p className="text-[13px] text-zinc-600 leading-relaxed mb-5">Sketch system architectures, user flows, brainstorming maps, and more — alongside your tasks and docs.</p>
          <ul className="text-left inline-block space-y-1.5 text-[12px] text-zinc-700 mb-5">
            <li className="flex items-start gap-2"><Sparkle size={12} weight="fill" className="text-violet-500 mt-0.5" /> 30+ shapes & smart connectors</li>
            <li className="flex items-start gap-2"><Sparkle size={12} weight="fill" className="text-violet-500 mt-0.5" /> Auto-save every couple of seconds</li>
            <li className="flex items-start gap-2"><Sparkle size={12} weight="fill" className="text-violet-500 mt-0.5" /> Export to PNG / JSON anytime</li>
          </ul>
          <Button onClick={() => navigate('/billing')} className="bg-violet-600 hover:bg-violet-700" data-testid="whiteboard-upgrade-cta">
            <Crown size={14} weight="fill" className="mr-1.5" /> Upgrade to Business
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 h-full flex flex-col bg-white" data-testid="whiteboard-view">
      {/* ─── Board tab strip ─── (multi-board switcher) */}
      <div className="px-3 py-1.5 border-b border-zinc-200 flex items-center gap-1 bg-zinc-50/80 overflow-x-auto flex-shrink-0">
        {boards.map(b => {
          const active = b.board_id === currentBoardId;
          return (
            <div key={b.board_id}
              className={`group relative flex items-center gap-1 pl-3 pr-1 h-7 rounded-md text-[12px] cursor-pointer flex-shrink-0 transition-colors
                ${active ? 'bg-indigo-50 border border-indigo-500 text-indigo-700 font-semibold shadow-sm' : 'border border-transparent text-zinc-600 hover:bg-white hover:border-zinc-200'}`}
              onClick={() => switchBoard(b.board_id)}
              data-testid={`wb-tab-${b.board_id}`}
              title={b.name}>
              <PencilSimple size={10} className={active ? 'text-indigo-500' : 'text-zinc-400'} weight={active ? 'fill' : 'regular'} />
              {active ? (
                <input
                  value={boardName}
                  onChange={e => setBoardName(e.target.value)}
                  onClick={e => e.stopPropagation()}
                  onKeyDown={e => { if (e.key === 'Enter') e.target.blur(); }}
                  disabled={!canEdit}
                  className="bg-transparent outline-none border-0 text-[12px] font-semibold text-indigo-700 max-w-[180px] min-w-[60px]"
                  style={{ width: `${Math.max(60, (boardName?.length || 8) * 8)}px` }}
                  data-testid="wb-board-name"
                />
              ) : (
                <span className="max-w-[160px] truncate">{b.name || 'Untitled'}</span>
              )}
              {boards.length > 1 && (
                <button
                  onClick={(e) => { e.stopPropagation(); deleteBoard(b.board_id); }}
                  className="w-4 h-4 ml-1 rounded flex items-center justify-center opacity-0 group-hover:opacity-100 text-zinc-400 hover:text-rose-600 hover:bg-rose-50 transition-all"
                  title="Delete board"
                  data-testid={`wb-tab-delete-${b.board_id}`}>
                  <XIcon size={10} weight="bold" />
                </button>
              )}
            </div>
          );
        })}
        <button
          onClick={createBoard}
          disabled={boards.length >= maxBoards}
          className="flex items-center gap-1 h-7 px-2 rounded-md text-[12px] text-indigo-600 hover:bg-indigo-50 disabled:text-zinc-300 disabled:cursor-not-allowed transition-colors flex-shrink-0"
          title={boards.length >= maxBoards ? `Plan limit reached (${maxBoards})` : 'New board'}
          data-testid="wb-new-board">
          <span className="text-base leading-none">+</span> New board
        </button>
        <div className="ml-auto text-[10px] text-zinc-400 pr-2 flex-shrink-0">{boards.length} / {maxBoards} boards</div>
      </div>

      {/* Top toolbar */}
      <div className="px-4 py-2 border-b border-zinc-200 flex items-center justify-between flex-shrink-0 gap-3 bg-white">
        <div className="flex items-center gap-2 flex-shrink-0">
          <span className="text-[11px] text-zinc-400 whitespace-nowrap" data-testid="wb-counts">{nodes.length} · {edges.length}</span>
        </div>

        {/* ─── Inline format strip (Lucid-style — always visible, single row) ─── */}
        <div className="flex-1 flex items-center justify-center min-w-0 overflow-x-auto whitespace-nowrap">
          <FormatBar
            node={selectedNode} edge={selectedEdge}
            setNodeProp={setNodeProp} setEdgeProp={setEdgeProp}
            duplicate={duplicateNode} removeNode={removeNodeById} removeEdge={deleteEdge}
          />
        </div>

        <div className="flex items-center gap-1.5 flex-shrink-0">
          <div className="text-[11px] tabular-nums mr-2 min-w-[110px] text-right" data-testid="whiteboard-save-status">
            {savingState === 'saving' && <span className="text-zinc-500 inline-flex items-center gap-1"><ArrowsClockwise size={11} className="animate-spin" /> Saving…</span>}
            {savingState === 'saved' && <span className="text-emerald-600 inline-flex items-center gap-1"><FloppyDisk size={11} weight="fill" /> Saved</span>}
            {savingState === 'error' && <span className="text-red-600">Save failed — retry</span>}
            {savingState === 'idle' && lastSavedAt && <span className="text-zinc-400">Last saved {new Date(lastSavedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>}
          </div>
          <Button variant="ghost" size="sm" className="h-7 text-[11px]" onClick={undo} disabled={!historyRef.current.past.length} title="Undo (Ctrl+Z)" data-testid="wb-undo"><ArrowUUpLeft size={12} /></Button>
          <Button variant="ghost" size="sm" className="h-7 text-[11px]" onClick={redo} disabled={!historyRef.current.future.length} title="Redo (Ctrl+Shift+Z)" data-testid="wb-redo"><ArrowUUpRight size={12} /></Button>
          <div className="w-px h-4 bg-zinc-200 mx-1" />
          <Button variant="ghost" size="sm" className="h-7 text-[11px]" onClick={() => setShowTemplates(true)} data-testid="wb-templates-btn"><GitBranch size={12} className="mr-1" /> Templates</Button>
          <Button variant="ghost" size="sm" className="h-7 text-[11px]" onClick={fitView} data-testid="wb-fit-btn"><FrameCorners size={12} className="mr-1" /> Fit</Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="h-7 text-[11px]" data-testid="wb-export-btn">
                <DownloadSimple size={12} className="mr-1" /> Export <CaretDown size={10} className="ml-1" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-44">
              <DropdownMenuItem onClick={exportPng} data-testid="wb-export-png" className="text-sm cursor-pointer">
                <DownloadSimple size={12} className="mr-2" /> Export as PNG
              </DropdownMenuItem>
              <DropdownMenuItem onClick={exportPdf} data-testid="wb-export-pdf" className="text-sm cursor-pointer">
                <FilePdf size={12} className="mr-2" /> Export as PDF
              </DropdownMenuItem>
              <DropdownMenuItem onClick={exportJson} data-testid="wb-export-json" className="text-sm cursor-pointer">
                <Code size={12} className="mr-2" /> Export as JSON
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          {canEdit && <Button variant="ghost" size="sm" className="h-7 text-[11px] text-rose-600 hover:text-rose-700 hover:bg-rose-50" onClick={clearBoard} data-testid="wb-clear"><Trash size={12} className="mr-1" /> Clear</Button>}
        </div>
      </div>

      <div className="flex-1 flex min-h-0 relative">
        {/* Categorized palette */}
        {canEdit && palCollapsed && (
          <button onClick={togglePalette} title="Show shapes panel"
            className="w-7 h-16 absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-white border border-zinc-200 rounded-r-lg shadow-md flex items-center justify-center text-zinc-500 hover:text-indigo-600 hover:bg-indigo-50 transition-colors"
            data-testid="wb-palette-show">
            <CaretRight size={14} weight="bold" />
          </button>
        )}
        {canEdit && !palCollapsed && (
          <aside className="w-[220px] border-r border-zinc-200 bg-zinc-50/60 flex flex-col flex-shrink-0" data-testid="wb-palette">
            <div className="p-2.5 border-b border-zinc-200 flex items-center gap-1.5">
              <div className="relative flex-1">
                <MagnifyingGlass size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-zinc-400" />
                <Input value={paletteSearch} onChange={e => setPaletteSearch(e.target.value)} placeholder="Search shapes…"
                  className="h-7 text-[11px] pl-7 bg-white" data-testid="wb-palette-search" />
              </div>
              <button onClick={togglePalette} title="Collapse panel"
                className="w-7 h-7 rounded flex items-center justify-center text-zinc-400 hover:text-indigo-600 hover:bg-indigo-50 transition-colors flex-shrink-0"
                data-testid="wb-palette-collapse">
                <CaretLeft size={12} weight="bold" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto py-1">
              {filteredLibrary.map(group => {
                const collapsed = !!collapsedGroups[group.id];
                return (
                  <div key={group.id} className="mb-1">
                    <button
                      onClick={() => setCollapsedGroups(s => ({ ...s, [group.id]: !s[group.id] }))}
                      className="w-full px-3 py-1.5 flex items-center justify-between text-[10px] uppercase tracking-wider font-bold text-zinc-500 hover:bg-zinc-100"
                      data-testid={`wb-palette-group-${group.id}`}>
                      <span>{group.label} <span className="text-zinc-400 font-normal normal-case tracking-normal">· {group.items.length}</span></span>
                      {collapsed ? <CaretRight size={10} weight="bold" /> : <CaretDown size={10} weight="bold" />}
                    </button>
                    {!collapsed && (
                      <div className="grid grid-cols-3 gap-1.5 px-2 py-1.5">
                        {group.items.map(item => (
                          <button
                            key={`${group.id}-${item.name}`}
                            draggable
                            onDragStart={(e) => onDragStart(e, item)}
                            onClick={() => addNode(item)}
                            title={`${item.name} — drag onto canvas or click to add`}
                            className="aspect-[5/4] bg-white border border-zinc-200 hover:border-indigo-400 hover:shadow-md rounded-md flex flex-col items-center justify-center gap-0.5 cursor-grab active:cursor-grabbing transition-all p-1"
                            data-testid={`wb-shape-${(item.shape || item.kind)}-${item.color}`}
                          >
                            <ShapePreview item={item} />
                            <span className="text-[8px] text-zinc-500 leading-none truncate max-w-full">{item.name}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
              {filteredLibrary.length === 0 && (
                <div className="text-[11px] text-zinc-400 text-center px-4 py-8">No shapes match "{paletteSearch}"</div>
              )}
            </div>
            <div className="px-3 py-2 border-t border-zinc-200 text-[9px] text-zinc-400 leading-snug">
              Drag onto canvas, or click to add at the center
            </div>
          </aside>
        )}

        {/* React Flow canvas */}
        <div ref={flowWrapper} className="flex-1 relative" onDragOver={onDragOver} onDrop={onDrop}>
          {loading ? (
            <div className="h-full flex items-center justify-center text-[12px] text-zinc-400">Loading canvas…</div>
          ) : (
            <ReactFlow
              nodes={nodesWithEditing}
              edges={edgesWithCallbacks}
              onNodesChange={onNodesChange}
              onEdgesChange={onEdgesChange}
              onConnect={onConnect}
              onNodeDoubleClick={onNodeDoubleClick}
              onEdgeDoubleClick={onEdgeDoubleClick}
              onNodeDragStart={onNodeDragStart}
              onInit={(instance) => { rfInstance.current = instance; }}
              nodeTypes={NODE_TYPES}
              edgeTypes={EDGE_TYPES}
              defaultEdgeOptions={defaultEdgeOptions}
              connectionMode={ConnectionMode.Loose}
              connectionLineStyle={{ stroke: '#6366f1', strokeWidth: 2 }}
              connectionRadius={30}
              snapToGrid snapGrid={[8, 8]}
              fitView
              proOptions={{ hideAttribution: true }}
              minZoom={0.2} maxZoom={2}
              deleteKeyCode={['Backspace', 'Delete']}
              // Drag-to-marquee on empty canvas (Miro/Lucid style). Pan only with
              // middle-click or right-click drag; left-click drag = selection rect.
              selectionOnDrag
              panOnDrag={[1, 2]}
              selectionMode="partial"
              multiSelectionKeyCode={['Shift', 'Meta', 'Control']}
            >
              <Background gap={16} size={1} color="#e4e4e7" />
              <Controls position="bottom-right" showInteractive={false} className="!shadow-md !border !border-zinc-200 !rounded-lg overflow-hidden" />
              {nodes.length === 0 && !showTemplates && (
                <Panel position="top-center" className="!top-[40%]">
                  <div className="bg-white border border-zinc-200 rounded-xl shadow-lg px-6 py-5 text-center max-w-sm">
                    <div className="w-10 h-10 mx-auto mb-3 rounded-lg bg-indigo-100 flex items-center justify-center"><PencilSimple size={20} className="text-indigo-600" weight="bold" /></div>
                    <div className="text-[14px] font-bold text-zinc-800 mb-1">Start your whiteboard</div>
                    <div className="text-[12px] text-zinc-500 mb-4 leading-relaxed">Drag a shape from the left palette onto the canvas, or pick a template.</div>
                    <Button size="sm" className="bg-indigo-600 hover:bg-indigo-700" onClick={() => setShowTemplates(true)} data-testid="wb-empty-templates"><GitBranch size={12} className="mr-1.5" /> Browse templates</Button>
                  </div>
                </Panel>
              )}
            </ReactFlow>
          )}

          {/* Templates picker */}
          {showTemplates && (
            <div className="absolute inset-0 bg-black/30 flex items-center justify-center p-6 z-10" onClick={() => setShowTemplates(false)}>
              <div className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-2xl" onClick={e => e.stopPropagation()}>
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="font-['Outfit'] text-[18px] font-bold text-zinc-900">Pick a starting point</h3>
                    <p className="text-[12px] text-zinc-500">Templates pre-fill the canvas with shapes you can rearrange or delete.</p>
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => setShowTemplates(false)} data-testid="wb-templates-close">Close</Button>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { key: 'flowchart',    icon: GitBranch,    color: 'from-indigo-50 to-violet-50',  desc: 'Decision flow with start, process & end nodes.' },
                    { key: 'architecture', icon: PencilSimple, color: 'from-violet-50 to-fuchsia-50', desc: 'Web/mobile app + API gateway + services + DB.' },
                    { key: 'retro',        icon: Note,         color: 'from-amber-50 to-orange-50',   desc: "What went well · What didn't · Action items." },
                    { key: 'blank',        icon: TextT,        color: 'from-zinc-50 to-slate-50',     desc: 'Empty canvas — bring your own ideas.' },
                  ].map(t => {
                    const tpl = TEMPLATES[t.key];
                    return (
                      <button key={t.key} onClick={() => loadTemplate(t.key)}
                        className={`text-left p-4 rounded-xl border border-zinc-200 hover:border-indigo-400 hover:shadow-md transition-all bg-gradient-to-br ${t.color}`}
                        data-testid={`wb-template-${t.key}`}>
                        <div className="flex items-center gap-2 mb-1.5">
                          <t.icon size={14} className="text-indigo-600" weight="bold" />
                          <span className="text-[14px] font-bold text-zinc-800">{tpl.name}</span>
                        </div>
                        <p className="text-[11px] text-zinc-600 leading-relaxed">{t.desc}</p>
                        <div className="text-[10px] text-zinc-400 mt-2">{tpl.nodes.length} shapes · {tpl.edges.length} connectors</div>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          <InlineLabelDialog open={!!editingEdge} value={editingEdge?.value} onChange={applyEdgeLabel} onClose={() => setEditingEdge(null)} />
        </div>
      </div>

      {/* Footer hint */}
      <div className="px-4 py-1.5 border-t border-zinc-100 bg-zinc-50/60 flex items-center gap-3 text-[10px] text-zinc-500 flex-shrink-0 flex-wrap">
        <span><kbd className="px-1 py-0.5 bg-white border border-zinc-200 rounded font-mono text-[9px]">double-click</kbd> a shape to edit text</span>
        <span><kbd className="px-1 py-0.5 bg-white border border-zinc-200 rounded font-mono text-[9px]">drag</kbd> blue dot to connect</span>
        <span><kbd className="px-1 py-0.5 bg-white border border-zinc-200 rounded font-mono text-[9px]">drag</kbd> corners to resize</span>
        <span><kbd className="px-1 py-0.5 bg-white border border-zinc-200 rounded font-mono text-[9px]">Ctrl+Z</kbd> undo</span>
        <span><kbd className="px-1 py-0.5 bg-white border border-zinc-200 rounded font-mono text-[9px]">Ctrl+C</kbd>/<kbd className="px-1 py-0.5 bg-white border border-zinc-200 rounded font-mono text-[9px]">V</kbd> copy/paste</span>
        <span><kbd className="px-1 py-0.5 bg-white border border-zinc-200 rounded font-mono text-[9px]">Del</kbd> remove</span>
      </div>
      {/* Handle hover-grow + edge hover-thicken styles */}
      <style>{`
        .wb-handle { opacity: 0; transition: opacity 120ms ease, transform 120ms ease, background 120ms ease; pointer-events: auto !important; }
        .react-flow__node:hover .wb-handle,
        .react-flow__node.selected .wb-handle,
        .react-flow__node:focus-within .wb-handle,
        .react-flow__node[data-selected="true"] .wb-handle { opacity: 1; }
        .wb-handle:hover { transform: scale(1.45); background: #4f46e5 !important; }
        .react-flow__edge-path { transition: stroke-width 120ms; }
        .react-flow__edge:hover .react-flow__edge-path { stroke-width: 3 !important; }
      `}</style>
    </div>
  );
}
